import React, { useState, useEffect } from "react";

import background_image from "@/assets/images/obralink-background.png";
import useFetch from "@/hooks/useFetch";

import { BsFillEyeFill, BsFillEyeSlashFill } from "react-icons/bs";
import i18n from "@/i18n/i18n";
import { NestedLayout } from "@/components/nested-layout";
import logo_light from "@/assets/images/logo_light.svg";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import { useDeploymentUrls } from "@/contexts/DeploymentUrlsContext";
import { useAuthContext } from "@/contexts/AuthContext";


const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const { deploymentUrls } = useDeploymentUrls();
  const { setSession, setEnvironment } = useAuthContext();

  const { data, loading, error, execute, status } = useFetch<any>({
    url: "/login/",
    method: "POST",
    body: JSON.stringify({ username, password }),
    immediate: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const EXTERNAL_URLS = {
    es: {
      signIn: "https://obralink.com/es-CL/sign-in/",
      tryDemo: "https://obralink.com/es-CL/try-demo/",
    },
    en: {
      signIn: "https://obralink.com/en-US/sign-in/",
      tryDemo: "https://obralink.com/en-US/try-demo/",
    },
  };

  const [loginError, setLoginError] = useState<string | null>(null);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleEditUsername = () => {
    const lang = i18n.language;
    window.location.href = EXTERNAL_URLS[lang === "es" ? "es" : "en"].signIn;
  };

  const handleRegister = () => {
    const lang = i18n.language;
    window.location.href = EXTERNAL_URLS[lang === "es" ? "es" : "en"].tryDemo;
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const email =
      searchParams.get("email") || localStorage.getItem("user_email");
    setUsername(email || "");

    const env = searchParams.get("environment") || "";
    setEnvironment(env);

    const lang = searchParams.get("lang");
    if (lang) {
      i18n.changeLanguage(lang);
    }
  }, []);

  useEffect(() => {
    if (data && status === 200) {
      const { csrf_token, jwt } = data;

      setSession({
        access_token: jwt.access,
        refresh_token: jwt.refresh,
        csrf_token: csrf_token,
      });

      window.history.replaceState({}, "", `${window.location.pathname}`);
    }
  }, [data, status]);

  useEffect(() => {
    if (error) {
      switch (status) {
        case 400:
          setLoginError(t("login.invalid_credentials"));
          break;
        case 401:
          setLoginError(t("login.invalid_credentials"));
          // Server is not making a distinction between unauthorized and invalid credentials for now
          // setLoginError(t("login.unauthorized"));
          break;
        case 403:
          setLoginError(t("login.forbidden"));
          break;
        case 404:
          setLoginError(t("login.user_not_found"));
          break;
        case 429:
          setLoginError(t("login.too_many_attempts"));
          break;
        case 500:
          setLoginError(t("login.server_error"));
          break;
        default:
          setLoginError(t("login.general_error"));
      }
    }
  }, [error, status, t]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationError("");
    setIsEditing(false);

    if (!username.trim()) {
      setValidationError(t("login.username_required"));
      return;
    }

    if (!password.trim()) {
      setValidationError(t("login.password_required"));
      return;
    }

    setIsSubmitting(true);
    try {
      await execute();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setIsEditing(true);
    if (loginError) {
      setLoginError(null);
    }
  };

  return (
    <>
      <div
        id="navbar"
        className="fixed z-50 flex w-full justify-between py-1 text-sm backdrop-blur-md"
        role="banner"
      >
        <NestedLayout className="my-0 flex w-full items-center justify-between">
          <a href="https://obralink.com/es-CL/" aria-label="Obralink home">
            <img
              src={logo_light}
              alt="Obralink Logo"
              width={100}
              height={100}
            />
          </a>
        </NestedLayout>
      </div>
      <NestedLayout
        className="dark min-h-screen flex flex-col justify-center items-center w-full relative bg-cover bg-center py-20"
        style={{
          backgroundImage: `url(${background_image})`,
          backgroundSize: "cover",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-dark/40 to-primary-dark/70"></div>
        <div className="relative z-10 mx-4 bg-primary-light/10 px-8 py-6 rounded-2xl shadow-2xl flex flex-col items-center w-full max-w-md backdrop-blur-md border border-primary-light/20 animate-fadeIn">
          <div className="flex flex-col items-center justify-center w-full space-y-8">
            <h1 className="text-3xl font-bold text-primary-light bg-gradient-to-r from-primary-light to-gray-300 bg-clip-text text-transparent pb-2">
              {t("login.submit")}
            </h1>
            <form
              onSubmit={handleSubmit}
              className="w-full space-y-6"
              noValidate
            >
              <div className="flex items-center text-primary-light justify-between">
                {username ? (
                  <div className="flex flex-col items-center justify-center w-full space-y-3">
                    <div className="relative flex w-full mb-2">
                      <div className="flex items-center gap-3 justify-start w-full">
                        <span
                          className="text-sm text-gray-400"
                          aria-label={t("login.username")}
                        >
                          {t("login.username")}
                        </span>
                        <span className="font-medium text-nowrap text-primary-light">
                          {username}
                        </span>
                        <button
                          onClick={handleEditUsername}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary-light focus:outline-none z-10 transition-colors duration-300"
                          type="button"
                          aria-label={t("login.edit")}
                        >
                          <FaEdit className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-4 w-full">
                      <div className="flex flex-col">
                        <div className="relative">
                          <input
                            name="password"
                            type={showPassword ? "text" : "password"}
                            className="w-full text-base p-2.5 border border-gray-300/30 rounded-lg bg-white/5 focus:outline-none focus:ring-2 focus:ring-primary-light/50 focus:border-transparent text-primary-light placeholder-gray-400 transition-all duration-300 ease-in-out"
                            value={password}
                            onChange={handlePasswordChange}
                            placeholder={t("login.password")}
                            autoComplete="current-password"
                            required
                            aria-label={t("login.password")}
                          />
                          <button
                            type="button"
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-light focus:outline-none transition-colors duration-300"
                            onClick={togglePasswordVisibility}
                            aria-label={
                              showPassword
                                ? t("login.hide_password")
                                : t("login.show_password")
                            }
                          >
                            {showPassword ? (
                              <BsFillEyeFill />
                            ) : (
                              <BsFillEyeSlashFill />
                            )}
                          </button>
                        </div>
                        <div className="flex justify-end w-full">
                          {deploymentUrls?.apiUrl && (
                            <a
                              className="text-[13px] text-secondary-yellow hover:text-secondary-yellow/80 transition-colors duration-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-light/50 rounded px-2 py-1 text-center"
                              href={`${deploymentUrls?.apiUrl}/system/sdx/users/recover/send/`}
                            >
                              {t("navbar.change_password")}
                            </a>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col space-y-2 pt-2">
                        <button
                          type="button"
                          onClick={handleRegister}
                          className="text-sm text-gray-400 hover:text-primary-light transition-colors duration-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-light/50 rounded px-2 py-1"
                        >
                          {t("login.not_registered")}
                        </button>
                      </div>
                    </div>
                    {(!loginError || isEditing) && (
                      <button
                        type="submit"
                        className={`
                          w-full flex justify-center items-center gap-2
                          py-3.5 px-12 mt-6
                          text-primary-dark font-medium
                          bg-gradient-to-r bg-secondary-yellow 
                          hover:bg-secondary-yellow/90
                          rounded-xl transition-all duration-300 ease-in-out
                          transform hover:scale-105 hover:shadow-lg
                          focus:outline-none focus:ring-2 focus:ring-primary-light/50
                          disabled:opacity-50 disabled:cursor-not-allowed
                          disabled:hover:scale-100
                          ${isSubmitting ? "cursor-wait animate-pulse" : ""}
                        `}
                        disabled={isSubmitting}
                        aria-busy={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>{t("login.loading")}</>
                        ) : (
                          t("login.continue")
                        )}
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={handleEditUsername}
                    className="w-full text-left text-gray-400 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-light rounded px-2"
                  >
                    {t("login.no_username")}
                  </button>
                )}
              </div>
            </form>

            {validationError && (
              <p className="text-red-500 mt-2 text-sm" role="alert">
                {validationError}
              </p>
            )}

            {loginError && (
              <p className="text-red-500 mt-4 text-sm" role="alert">
                {typeof loginError === "string"
                  ? t("login.error", { error: loginError })
                  : t("login.general_error")}
              </p>
            )}
          </div>
        </div>
      </NestedLayout>
    </>
  );
};

export default Login;
